import React from "react";
import "./data-table.scss";

const getRowKey = (row, rowKey, index) => {
  if (typeof rowKey === "function") return rowKey(row, index);
  if (row && row[rowKey] != null) return row[rowKey];
  return index;
};

const DataTable = ({
  columns = [],
  rows = [],
  rowKey = "id",
  loading = false,
  loadingMessage = "Loading...",
  emptyMessage = "No records found.",
  minWidth,
  onRowClick,
  rowClassName,
}) => {
  const colSpan = Math.max(columns.length, 1);

  return (
    <div className="data-table">
      <table style={minWidth ? { minWidth: `${minWidth}px` } : undefined}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={column.headerClassName}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={colSpan} className="data-table__message">
                {loadingMessage}
              </td>
            </tr>
          )}
          {!loading && !rows.length && (
            <tr>
              <td colSpan={colSpan} className="data-table__message">
                {emptyMessage}
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((row, index) => {
              const extraClass = typeof rowClassName === "function" ? rowClassName(row, index) : rowClassName;
              return (
                <tr
                  key={getRowKey(row, rowKey, index)}
                  className={[onRowClick && "data-table__row--clickable", extraClass].filter(Boolean).join(" ")}
                  onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                >
                  {columns.map((column) => (
                    <td key={column.key} className={column.className}>
                      {column.render ? column.render(row, index) : (row?.[column.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

export const DataTableActions = ({ children }) => (
  <div className="data-table__actions">{children}</div>
);

const positiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export const DataTableFooter = ({ meta, page, loading = false, onPageChange, noun }) => {
  const currentPage = positiveNumber(meta?.current_page) || page || 1;
  const lastPage = Math.max(1, positiveNumber(meta?.last_page) || 1);
  const total = Number(meta?.total) || 0;
  const perPage = positiveNumber(meta?.per_page) || 0;
  const derivedFrom = total ? (currentPage - 1) * (perPage || total) + 1 : 0;
  const derivedTo = total ? (perPage ? Math.min(currentPage * perPage, total) : total) : 0;
  const from = total ? positiveNumber(meta?.from) || derivedFrom : 0;
  const to = total ? positiveNumber(meta?.to) || derivedTo : 0;

  return (
    <footer className="data-table__footer">
      <span>
        Showing {from}–{to} of {total}
        {noun ? ` ${noun}` : ""}
      </span>
      <div className="data-table__pager">
        <button
          type="button"
          disabled={currentPage <= 1 || loading}
          onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
        >
          Previous
        </button>
        <span>
          Page {currentPage} / {lastPage}
        </span>
        <button
          type="button"
          disabled={currentPage >= lastPage || loading}
          onClick={() => onPageChange?.(currentPage + 1)}
        >
          Next
        </button>
      </div>
    </footer>
  );
};

export default DataTable;
